import React, { useState, useEffect, useRef } from 'react';
import { postData, getData, serverURL } from '../../../../Services/Fetchnodeservices';

const getThemeStyles = (isDark) => ({
  // Primary colors - Blue theme
  bgPrimary: isDark ? '#0A1628' : '#F0F7FF',
  bgSecondary: isDark ? '#0F2440' : '#FFFFFF',
  bgTertiary: isDark ? '#1A365D' : '#E8F4FD',
  bgCard: isDark ? '#13294B' : '#FFFFFF',
  bgInput: isDark ? '#1A365D' : '#F7FBFF',
  bgHover: isDark ? '#1E3A6E' : '#E3F0FF',
  
  // Text colors
  textPrimary: isDark ? '#E8F4FD' : '#0A1628',
  textSecondary: isDark ? '#93BBFC' : '#1E5B9E',
  textMuted: isDark ? '#5A8BC9' : '#6B8FB3',
  textLight: isDark ? '#A8C8F0' : '#3A7AB5',
  
  // Border colors
  border: isDark ? '#1E3A6E' : '#B8D4F0',
  borderLight: isDark ? '#2A4A7A' : '#D6E8F8',
  borderFocus: isDark ? '#4A8AD4' : '#2B7BCB',
  
  // Accent colors
  primary: '#2B7BCB',
  primaryDark: '#1A5A9E',
  primaryLight: '#E3F0FF',
  primaryGradient: 'linear-gradient(135deg, #2B7BCB 0%, #1A5A9E 100%)',
  
  // Status colors
  success: '#10B981',
  successBg: isDark ? '#0A2A1A' : '#ECFDF5',
  warning: '#F59E0B',
  warningBg: isDark ? '#2A1A0A' : '#FFFBEB',
  danger: '#EF4444',
  dangerBg: isDark ? '#2A0A0A' : '#FEF2F2',
  info: '#3B82F6',
  infoBg: isDark ? '#0A1A3A' : '#EFF6FF',
  
  // Shadows
  shadow: isDark ? '0 8px 30px rgba(0, 0, 0, 0.6)' : '0 8px 30px rgba(43, 123, 203, 0.12)',
  shadowHover: isDark ? '0 12px 40px rgba(0, 0, 0, 0.8)' : '0 12px 40px rgba(43, 123, 203, 0.2)',
  
  // Video/Poster
  videoBg: '#0A1628',
  posterBorder: isDark ? '#1E3A6E' : '#B8D4F0',
  
  // Scrollbar
  scrollbarTrack: isDark ? '#0F2440' : '#E8F4FD',
  scrollbarThumb: isDark ? '#1E3A6E' : '#B8D4F0',
  scrollbarThumbHover: isDark ? '#2B7BCB' : '#2B7BCB'
});

const WebContentManager = ({ isDark = true }) => {
  const theme = getThemeStyles(isDark);
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showForm, setShowForm] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const videoRefs = useRef({});
  
  // Form state
  const [formData, setFormData] = useState({
    video: null,
    poster: null,
    title1: '',
    subtitle1: '',
    title2: '',
    subtitle2: '',
    header: ''
  });

  // Fetch all content on component mount
  useEffect(() => {
    fetchContent();
  }, []);

  // Fetch content from server
  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await getData("Headercontent/fetch_webcontent");
      if (response && response.status) {
        setContentList(response.data || []);
        setMessage({ text: 'Content loaded successfully', type: 'success' });
      } else {
        setMessage({ text: response?.message || 'Failed to load content', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setMessage({ text: 'Server error while fetching content', type: 'error' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  // Handle file input changes
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files[0] || null
    }));
  };

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      video: null,
      poster: null,
      title1: '',
      subtitle1: '',
      title2: '',
      subtitle2: '',
      header: ''
    });
    document.getElementById('videoInput').value = '';
    document.getElementById('posterInput').value = '';
    setEditingId(null);
    setShowForm(false);
  };

  // Handle form submission for adding new content
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    const formDataToSend = new FormData();
    
    // Append files
    if (formData.video) {
      formDataToSend.append('video', formData.video);
    }
    if (formData.poster) {
      formDataToSend.append('poster', formData.poster);
    }
    
    // Append text fields
    formDataToSend.append('title1', formData.title1 || '');
    formDataToSend.append('subtitle1', formData.subtitle1 || '');
    formDataToSend.append('title2', formData.title2 || '');
    formDataToSend.append('subtitle2', formData.subtitle2 || '');
    formDataToSend.append('header', formData.header || '');

    try {
      const response = await postData("Headercontent/add_webcontent", formDataToSend, true);
      
      if (response && response.status) {
        setMessage({ 
          text: `${response.message} (ID: ${response.insertId || 'N/A'})`, 
          type: 'success' 
        });
        resetForm();
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to add content', type: 'error' });
      }
    } catch (error) {
      console.error('Error adding content:', error);
      setMessage({ text: 'Server error while adding content: ' + error.message, type: 'error' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  // Handle update content
  const handleUpdate = async (id) => {
    try {
      const formDataToSend = new FormData();
      
      // Append files if selected
      if (formData.video) {
        formDataToSend.append('video', formData.video);
      }
      if (formData.poster) {
        formDataToSend.append('poster', formData.poster);
      }
      
      // Append text fields
      formDataToSend.append('title1', formData.title1 || '');
      formDataToSend.append('subtitle1', formData.subtitle1 || '');
      formDataToSend.append('title2', formData.title2 || '');
      formDataToSend.append('subtitle2', formData.subtitle2 || '');
      formDataToSend.append('header', formData.header || '');
      formDataToSend.append('id', id);

      const response = await postData("Headercontent/update_webcontent", formDataToSend, true);
      
      if (response && response.status) {
        setMessage({ text: response.message || 'Updated successfully', type: 'success' });
        resetForm();
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to update content', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating content:', error);
      setMessage({ text: 'Server error while updating content: ' + error.message, type: 'error' });
    } finally {
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setStatusLoading(id);
    
    try {
      const response = await postData("Headercontent/change_webcontent_status", {
        id: id,
        status: newStatus
      });
      
      if (response && response.status) {
        setMessage({ 
          text: response.message || `Status ${newStatus === 1 ? 'Activated' : 'Deactivated'} successfully`, 
          type: 'success' 
        });
        await fetchContent();
      } else {
        setMessage({ text: response?.message || 'Failed to change status', type: 'error' });
      }
    } catch (error) {
      console.error('Error changing status:', error);
      setMessage({ text: 'Server error while changing status', type: 'error' });
    } finally {
      setStatusLoading(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setShowForm(true);
    setFormData({
      video: null,
      poster: null,
      title1: item.title1 || '',
      subtitle1: item.subtitle1 || '',
      title2: item.title2 || '',
      subtitle2: item.subtitle2 || '',
      header: item.header || ''
    });
    setMessage({ text: `Editing ID: ${item.id}`, type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleCancelEdit = () => {
    resetForm();
    setMessage({ text: 'Edit cancelled', type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const handlePreview = (fileName, type) => {
    if (!fileName) {
      setMessage({ text: 'No file to preview', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    const fileUrl = `${serverURL}/public/uploadnew/upload/${fileName}`;
    window.open(fileUrl, '_blank');
  };

  // Video player controls
  const handleVideoPlay = (id) => {
    if (playingVideo && playingVideo !== id) {
      const prevVideo = videoRefs.current[playingVideo];
      if (prevVideo) {
        prevVideo.pause();
      }
    }
    setPlayingVideo(playingVideo === id ? null : id);
  };

  // ============== CSS STYLES ==============
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1.5rem',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
      background: theme.bgPrimary,
      color: theme.textPrimary
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    heading: {
      fontWeight: '600',
      fontSize: '2.2rem',
      marginBottom: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: theme.textPrimary,
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    headingSmall: {
      fontSize: '1rem',
      fontWeight: '300',
      marginLeft: '8px',
      color: theme.textSecondary,
      WebkitTextFillColor: theme.textSecondary
    },
    icon: {
      marginRight: '6px',
      WebkitTextFillColor: theme.primary
    },
    themeToggle: {
      background: theme.bgTertiary,
      border: `1px solid ${theme.border}`,
      borderRadius: '30px',
      padding: '0.5rem 1.2rem',
      color: theme.textPrimary,
      cursor: 'pointer',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    addButton: {
      background: theme.primaryGradient,
      border: 'none',
      borderRadius: '30px',
      padding: '0.6rem 1.5rem',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '0.95rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      fontWeight: '500',
      boxShadow: '0 4px 15px rgba(43, 123, 203, 0.3)'
    },
    card: {
      background: theme.bgCard,
      borderRadius: '20px',
      padding: '1.8rem 2rem',
      marginBottom: '2.5rem',
      boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    cardTitle: {
      fontWeight: '500',
      fontSize: '1.4rem',
      margin: 0,
      color: theme.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem'
    },
    fullWidth: {
      gridColumn: '1 / -1'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    label: {
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.4px',
      color: theme.textSecondary,
      fontWeight: '600'
    },
    fileInputWrapper: {
      background: theme.bgInput,
      borderRadius: '12px',
      padding: '0.5rem 1rem',
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    fileInput: {
      background: 'transparent',
      border: 'none',
      fontSize: '0.9rem',
      width: '100%',
      padding: '6px 0',
      cursor: 'pointer',
      color: theme.textPrimary
    },
    textInput: {
      background: theme.bgInput,
      border: `2px solid ${theme.border}`,
      borderRadius: '12px',
      padding: '0.7rem 1rem',
      fontSize: '0.9rem',
      color: theme.textPrimary,
      transition: 'all 0.2s ease',
      outline: 'none',
      width: '100%'
    },
    textareaInput: {
      background: theme.bgInput,
      border: `2px solid ${theme.border}`,
      borderRadius: '12px',
      padding: '0.7rem 1rem',
      fontSize: '0.9rem',
      color: theme.textPrimary,
      transition: 'all 0.2s ease',
      outline: 'none',
      width: '100%',
      minHeight: '80px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    fileInputHint: {
      fontSize: '0.75rem',
      marginTop: '4px',
      color: theme.textMuted
    },
    submitGroup: {
      display: 'flex',
      gap: '10px',
      marginTop: '0.5rem',
      flexWrap: 'wrap'
    },
    btn: {
      border: 'none',
      padding: '0.7rem 2rem',
      borderRadius: '40px',
      fontWeight: '500',
      fontSize: '0.95rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: theme.bgTertiary,
      color: theme.textPrimary,
      border: `1px solid ${theme.border}`
    },
    btnPrimary: {
      background: theme.primaryGradient,
      borderColor: theme.primary,
      color: '#ffffff',
      boxShadow: '0 4px 15px rgba(43, 123, 203, 0.3)'
    },
    btnSuccess: {
      background: theme.success,
      borderColor: theme.success,
      color: '#ffffff',
      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
    },
    btnDanger: {
      background: theme.danger,
      borderColor: theme.danger,
      color: '#ffffff',
      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
    },
    btnSecondary: {
      padding: '0.4rem 1.4rem',
      fontSize: '0.85rem',
      background: theme.bgTertiary,
      border: `1px solid ${theme.border}`
    },
    btnDisabled: {
      opacity: '0.6',
      cursor: 'not-allowed'
    },
    btnToggle: {
      background: theme.bgTertiary,
      border: `1px solid ${theme.border}`,
      borderRadius: '30px',
      padding: '0.4rem 1.2rem',
      color: theme.textPrimary,
      cursor: 'pointer',
      fontSize: '0.85rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    statusMessage: {
      marginTop: '16px',
      padding: '12px 20px',
      borderRadius: '40px',
      animation: 'slideIn 0.3s ease',
      background: theme.bgTertiary,
      color: theme.textPrimary,
      borderLeft: `4px solid ${theme.primary}`
    },
    statusSuccess: {
      borderLeft: `4px solid ${theme.success}`,
      background: theme.successBg,
      color: isDark ? '#b8e6d4' : '#166534'
    },
    statusError: {
      borderLeft: `4px solid ${theme.danger}`,
      background: theme.dangerBg,
      color: isDark ? '#f0c8d0' : '#991b1b'
    },
    tableWrapper: {
      overflowX: 'auto',
      borderRadius: '16px',
      background: theme.bgSecondary,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.85rem',
      minWidth: '1000px'
    },
    th: {
      textAlign: 'left',
      padding: '0.8rem 1rem',
      fontWeight: '600',
      letterSpacing: '0.3px',
      background: isDark ? '#0A1A3A' : '#E8F4FD',
      color: theme.textSecondary,
      borderBottom: `2px solid ${theme.border}`,
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '0.8rem 1rem',
      verticalAlign: 'middle',
      color: theme.textPrimary,
      borderBottom: `1px solid ${theme.borderLight}`,
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    trHover: {
      background: theme.bgHover
    },
    emptyCell: {
      textAlign: 'center',
      padding: '2.5rem 1rem',
      color: theme.textMuted
    },
    loadingCell: {
      textAlign: 'center',
      padding: '2.5rem 1rem',
      color: theme.textMuted
    },
    badgeId: {
      background: theme.bgTertiary,
      padding: '2px 12px',
      borderRadius: '40px',
      fontWeight: '600',
      fontSize: '0.8rem',
      color: theme.primary,
      border: `1px solid ${theme.border}`
    },
    previewBtn: {
      background: 'transparent',
      border: `1px solid ${theme.border}`,
      color: theme.textSecondary,
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '2px 10px',
      transition: 'all 0.2s ease',
      borderRadius: '20px'
    },
    editBtn: {
      borderRadius: '30px',
      padding: '4px 14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '0.8rem',
      background: 'transparent',
      border: `1px solid ${theme.border}`,
      color: theme.textSecondary
    },
    editBtnActive: {
      background: theme.primaryGradient,
      borderColor: theme.primary,
      color: '#ffffff'
    },
    statusBtn: {
      borderRadius: '30px',
      padding: '4px 14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '0.75rem',
      fontWeight: '600',
      border: 'none',
      minWidth: '70px'
    },
    statusActive: {
      background: theme.success,
      color: '#ffffff'
    },
    statusInactive: {
      background: theme.danger,
      color: '#ffffff'
    },
    statusLoading: {
      background: theme.bgTertiary,
      color: theme.textMuted,
      cursor: 'not-allowed',
      opacity: '0.6'
    },
    actionButtons: {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center'
    },
    spinner: {
      display: 'inline-block',
      animation: 'spin 1s linear infinite'
    },
    spinnerSmall: {
      display: 'inline-block',
      animation: 'spin 1s linear infinite',
      fontSize: '0.8rem'
    },
    videoContainer: {
      position: 'relative',
      width: '120px',
      maxWidth: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      background: theme.videoBg,
      aspectRatio: '16/9',
      border: `2px solid ${theme.border}`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
    },
    videoElement: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      cursor: 'pointer',
      background: theme.videoBg
    },
    playButton: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(43, 123, 203, 0.9)',
      border: '2px solid white',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '18px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      zIndex: 2,
      boxShadow: '0 4px 15px rgba(43, 123, 203, 0.4)'
    },
    posterImage: {
      width: '60px',
      height: '60px',
      objectFit: 'cover',
      borderRadius: '8px',
      border: `2px solid ${theme.border}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    posterPlaceholder: {
      width: '60px',
      height: '60px',
      background: theme.bgTertiary,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textMuted,
      fontSize: '0.65rem',
      border: `2px dashed ${theme.border}`,
      textAlign: 'center',
      padding: '4px'
    },
    mediaCell: {
      minWidth: '130px'
    },
    posterCell: {
      minWidth: '80px'
    },
    statusCell: {
      minWidth: '80px'
    },
    formContainer: {
      display: showForm ? 'block' : 'none',
      animation: 'slideIn 0.3s ease'
    },
    textTruncate: {
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline-block'
    },
    dateText: {
      fontSize: '0.75rem',
      color: theme.textMuted
    }
  };

  // Component for poster display with error handling
  const PosterDisplay = ({ posterUrl, id }) => {
    const [imageError, setImageError] = useState(false);

    if (!posterUrl || imageError) {
      return <div style={styles.posterPlaceholder}>No poster</div>;
    }

    return (
      <img 
        src={posterUrl} 
        alt={`Poster ${id}`}
        style={styles.posterImage}
        onError={() => setImageError(true)}
      />
    );
  };

  // Component for video display with error handling
  const VideoDisplay = ({ videoUrl, id }) => {
    const [videoError, setVideoError] = useState(false);
    const isPlaying = playingVideo === id;

    if (!videoUrl || videoError) {
      return <div style={styles.posterPlaceholder}>No video</div>;
    }

    return (
      <div style={styles.videoContainer}>
        <video
          ref={el => videoRefs.current[id] = el}
          src={videoUrl}
          style={styles.videoElement}
          muted
          playsInline
          loop
          onClick={() => handleVideoPlay(id)}
          onError={() => setVideoError(true)}
        />
        {!isPlaying && (
          <div 
            style={styles.playButton}
            onClick={() => handleVideoPlay(id)}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translate(-50%, -50%) scale(1.15)';
              e.target.style.background = 'rgba(43, 123, 203, 1)';
              e.target.style.boxShadow = '0 4px 20px rgba(43, 123, 203, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translate(-50%, -50%) scale(1)';
              e.target.style.background = 'rgba(43, 123, 203, 0.9)';
              e.target.style.boxShadow = '0 4px 15px rgba(43, 123, 203, 0.4)';
            }}
          >
            ▶
          </div>
        )}
        {isPlaying && (
          <div 
            style={{
              ...styles.playButton,
              background: 'rgba(239, 68, 68, 0.9)',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)'
            }}
            onClick={() => handleVideoPlay(id)}
          >
            ❚❚
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.heading}>
          <span style={styles.icon}>🎬</span> Webcontent Manager
          <small style={styles.headingSmall}>Manage your content</small>
        </h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            style={styles.themeToggle}
            onClick={() => window.dispatchEvent(new CustomEvent('toggleTheme'))}
          >
            {isDark ? '☀️ Light' : '🌙 Dark'}
          </button>
          {!showForm && !editingId && (
            <button 
              style={styles.addButton}
              onClick={() => setShowForm(true)}
            >
              <span>➕</span> Add New
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Content Form */}
      <div style={styles.formContainer}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <span style={styles.icon}>{editingId ? '✏️' : '📤'}</span> 
              {editingId ? `Edit Content (ID: ${editingId})` : 'Add New Content'}
            </h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button 
                style={styles.btnToggle}
                onClick={handleCancelEdit}
              >
                {showForm ? '➖ Less' : '➕ More'}
              </button>
            </div>
          </div>
          
          <form onSubmit={editingId ? (e) => { e.preventDefault(); handleUpdate(editingId); } : handleSubmit}>
            <div style={styles.formGrid}>
              {/* Header Field */}
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>📝 Header</label>
                  <input
                    type="text"
                    name="header"
                    value={formData.header}
                    onChange={handleInputChange}
                    placeholder="Enter header text"
                    style={styles.textInput}
                    onFocus={(e) => {
                      e.target.style.borderColor = theme.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = theme.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Title 1 */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>📌 Title 1</label>
                <input
                  type="text"
                  name="title1"
                  value={formData.title1}
                  onChange={handleInputChange}
                  placeholder="Enter title 1"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = theme.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Subtitle 1 */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>📄 Subtitle 1</label>
                <input
                  type="text"
                  name="subtitle1"
                  value={formData.subtitle1}
                  onChange={handleInputChange}
                  placeholder="Enter subtitle 1"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = theme.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Title 2 */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>📌 Title 2</label>
                <input
                  type="text"
                  name="title2"
                  value={formData.title2}
                  onChange={handleInputChange}
                  placeholder="Enter title 2"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = theme.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Subtitle 2 */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>📄 Subtitle 2</label>
                <input
                  type="text"
                  name="subtitle2"
                  value={formData.subtitle2}
                  onChange={handleInputChange}
                  placeholder="Enter subtitle 2"
                  style={styles.textInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = theme.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Video File */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>🎥 Video File</label>
                <div style={styles.fileInputWrapper}>
                  <input
                    type="file"
                    id="videoInput"
                    name="video"
                    accept="video/*"
                    onChange={handleFileChange}
                    style={styles.fileInput}
                  />
                </div>
                {editingId && (
                  <div style={styles.fileInputHint}>Leave empty to keep existing video</div>
                )}
              </div>

              {/* Poster File */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>🖼️ Poster Image</label>
                <div style={styles.fileInputWrapper}>
                  <input
                    type="file"
                    id="posterInput"
                    name="poster"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={styles.fileInput}
                  />
                </div>
                {editingId && (
                  <div style={styles.fileInputHint}>Leave empty to keep existing poster</div>
                )}
              </div>
            </div>

            <div style={styles.submitGroup}>
              <button 
                type="submit" 
                style={{
                  ...styles.btn,
                  ...(editingId ? styles.btnSuccess : styles.btnPrimary),
                  ...(submitting ? styles.btnDisabled : {})
                }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span style={styles.spinner}>⏳</span> Processing...
                  </>
                ) : (
                  <>
                    <span style={styles.icon}>{editingId ? '💾' : '⬆️'}</span> 
                    {editingId ? 'Update' : 'Submit'}
                  </>
                )}
              </button>
            </div>

            {message.text && (
              <div style={{
                ...styles.statusMessage,
                ...(message.type === 'success' ? styles.statusSuccess : {}),
                ...(message.type === 'error' ? styles.statusError : {})
              }}>
                {message.text}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Content List Table */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            <span style={styles.icon}>📋</span> Content List
            <span style={{fontSize: '0.8rem', fontWeight: '300', marginLeft: '8px', color: theme.textMuted}}>
              ({contentList.length} items)
            </span>
          </h2>
          <button 
            style={{
              ...styles.btn,
              ...styles.btnSecondary
            }}
            onClick={fetchContent}
            disabled={loading}
          >
            <span style={styles.icon}>🔄</span> Refresh
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Header</th>
                <th style={styles.th}>Title 1</th>
                <th style={styles.th}>Subtitle 1</th>
                <th style={styles.th}>Title 2</th>
                <th style={styles.th}>Subtitle 2</th>
                <th style={styles.th}>Video</th>
                <th style={styles.th}>Poster</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th} style={{textAlign: 'center'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={styles.loadingCell}>
                    <span style={styles.spinner}>⏳</span> Loading content...
                  </td>
                </tr>
              ) : !contentList || contentList.length === 0 ? (
                <tr>
                  <td colSpan="11" style={styles.emptyCell}>
                    ✨ No content found. Click "Add New" to create your first entry!
                  </td>
                </tr>
              ) : (
                contentList.map((item) => {
                  const videoUrl = item.video ? `${serverURL}/public/uploadnew/upload/${item.video}` : null;
                  const posterUrl = item.poster ? `${serverURL}/public/uploadnew/upload/${item.poster}` : null;
                  const createdDate = item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A';
                  const isActive = item.status === 1;
                  const isStatusLoading = statusLoading === item.id;

                  return (
                    <tr 
                      key={item.id} 
                      style={editingId === item.id ? styles.trHover : {}}
                    >
                      <td style={styles.td}>
                        <span style={styles.badgeId}>#{item.id}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.header || ''}>
                          {item.header || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.title1 || ''}>
                          {item.title1 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.subtitle1 || ''}>
                          {item.subtitle1 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.title2 || ''}>
                          {item.title2 || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.textTruncate} title={item.subtitle2 || ''}>
                          {item.subtitle2 || '—'}
                        </span>
                      </td>
                      <td style={{...styles.td, ...styles.mediaCell}}>
                        <VideoDisplay videoUrl={videoUrl} id={item.id} />
                      </td>
                      <td style={{...styles.td, ...styles.posterCell}}>
                        <PosterDisplay posterUrl={posterUrl} id={item.id} />
                      </td>
                      <td style={{...styles.td, ...styles.statusCell}}>
                        <button
                          style={{
                            ...styles.statusBtn,
                            ...(isStatusLoading ? styles.statusLoading : {}),
                            ...(isActive ? styles.statusActive : styles.statusInactive)
                          }}
                          onClick={() => handleStatusToggle(item.id, item.status)}
                          disabled={isStatusLoading}
                          onMouseEnter={(e) => {
                            if (!isStatusLoading) {
                              e.target.style.transform = 'scale(1.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          {isStatusLoading ? (
                            <>
                              <span style={styles.spinnerSmall}>⏳</span> 
                            </>
                          ) : (
                            isActive ? '✅ Active' : '❌ Inactive'
                          )}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateText}>{createdDate}</span>
                      </td>
                      <td style={{...styles.td, textAlign: 'center'}}>
                        <div style={styles.actionButtons}>
                          <button 
                            style={{
                              ...styles.editBtn,
                              ...(editingId === item.id ? styles.editBtnActive : {})
                            }}
                            onClick={() => handleEditClick(item)}
                            onMouseEnter={(e) => {
                              if (editingId !== item.id) {
                                e.target.style.background = theme.bgHover;
                                e.target.style.borderColor = theme.primary;
                                e.target.style.color = theme.primary;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (editingId !== item.id) {
                                e.target.style.background = 'transparent';
                                e.target.style.borderColor = theme.border;
                                e.target.style.color = theme.textSecondary;
                              }
                            }}
                          >
                            {editingId === item.id ? '📝 Editing' : '✏️ Edit'}
                          </button>
                          {videoUrl && (
                            <button
                              style={styles.previewBtn}
                              onClick={() => handlePreview(item.video, 'video')}
                              onMouseEnter={(e) => {
                                e.target.style.background = theme.primary;
                                e.target.style.color = '#ffffff';
                                e.target.style.borderColor = theme.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                                e.target.style.color = theme.textSecondary;
                                e.target.style.borderColor = theme.border;
                              }}
                            >
                              🔗
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .file-input-wrapper:hover {
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'};
        }
        .preview-btn:hover {
          transform: scale(1.1) !important;
        }
        .edit-btn:hover {
          transform: scale(1.05) !important;
        }
        .btn-secondary:hover:not(:disabled) {
          background: ${theme.bgHover} !important;
          border-color: ${theme.primary} !important;
          color: ${theme.primary} !important;
        }
        .btn-primary:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 6px 25px rgba(43, 123, 203, 0.4) !important;
        }
        .btn-success:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 6px 25px rgba(16, 185, 129, 0.4) !important;
        }
        .btn-danger:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 6px 25px rgba(239, 68, 68, 0.4) !important;
        }
        .btn-toggle:hover:not(:disabled) {
          background: ${theme.bgHover} !important;
          border-color: ${theme.primary} !important;
          color: ${theme.primary} !important;
        }
        .status-btn:hover:not(:disabled) {
          transform: scale(1.05) !important;
        }
        ::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: ${theme.scrollbarTrack};
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme.scrollbarThumb};
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${theme.scrollbarThumbHover};
        }
        * {
          box-sizing: border-box;
        }
        input[type="file"]::file-selector-button {
          background: ${isDark ? '#1A365D' : '#E8F4FD'};
          border: none;
          padding: 6px 14px;
          border-radius: 40px;
          color: ${isDark ? '#E8F4FD' : '#1A5A9E'};
          font-weight: 500;
          margin-right: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        input[type="file"]::file-selector-button:hover {
          background: ${isDark ? '#1E3A6E' : '#D6E8F8'};
          color: ${theme.primary};
        }
        video::-webkit-media-controls {
          display: none !important;
        }
        video::-webkit-media-controls-enclosure {
          display: none !important;
        }
        input, textarea {
          transition: all 0.2s ease;
        }
        input:focus, textarea:focus {
          border-color: ${theme.primary} !important;
          outline: none;
          box-shadow: 0 0 0 3px ${isDark ? 'rgba(43, 123, 203, 0.2)' : 'rgba(43, 123, 203, 0.15)'};
        }
        .add-btn:hover {
          transform: scale(1.02);
          box-shadow: 0 6px 25px rgba(43, 123, 203, 0.4) !important;
        }
      `}</style>
    </div>
  );
};

export default WebContentManager;